import React from "react";
import { Modal, Tabs, Tab, Row, Col, Card, Badge, ListGroup } from "react-bootstrap";
import { IHistory } from "../../interfaces/history";
import "./DetailsModal.css";

/** ===== Helpers comuns ===== */
const brl = (n?: number | null) =>
    typeof n === "number"
        ? n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
        : "—";

const statusToClass = (txt?: string | null) => {
    const v = (txt || "").toUpperCase();
    if (v.includes("NEG") || v.includes("REPROV")) return "bg-danger-dark";
    if (v.includes("APROV")) return "bg-accent";
    if (v.includes("NÃO APLIC") || v.includes("NAO APLIC")) return "bg-muted";
    return "bg-muted";
};

const safeParse = (s?: string | null) => {
    if (!s) return null;
    try {
        return JSON.parse(String(s).trim());
    } catch {
        return null;
    }
};

const toObj = (x: unknown) => {
    if (!x) return null;
    if (typeof x === "object") return x as any;
    if (typeof x === "string") {
        try { return JSON.parse(x); } catch { return null; }
    }
    return null;
};

const normalizeDotNetDate = (s: string) => {
    const m = /\/Date\((\d+)\)\//.exec(s);
    return m ? new Date(Number(m[1])).toISOString() : s;
};

const fmtDate = (iso?: string | Date | null) => {
    if (!iso) return "—";
    const d = typeof iso === "string" ? new Date(iso) : iso;
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("pt-BR");
};

const allowMeStatusBadgeClass = (status?: number | null) => {
    if (status === 1) return "bg-accent";
    if (status === 3) return "bg-danger-dark";
    if (status === 2) return "bg-warning";
    return "bg-muted";
};
const allowMeStatusLabel = (status?: number | null) =>
    typeof status === "number" ? String(status) : "—";

const Field = ({ label, value }: { label: string; value?: React.ReactNode }) => (
    <div className="dm-field">
        <small className="dm-label">{label}</small>
        <div className="dm-value">{value ?? "—"}</div>
    </div>
);

/** ===== Suporte para imagens/documentos (AllowMe) ===== */
const API_BASE = (process.env.REACT_APP_API_URL || "")
    .replace(/\/+$/, "")
    .replace(/\/api$/, "");
const toAbs = (u?: string) => {
    if (!u) return "";
    return /^https?:\/\//i.test(u) ? u : `${API_BASE}${u.startsWith("/") ? "" : "/"}${u}`;
};
const isPdf = (u: string) => u.toLowerCase().endsWith(".pdf");
function slugify(s: string) {
    return (s || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
}

function Gallery({ urls, title, idBase }: { urls?: string[]; title: string; idBase: string }) {
    if (!urls || !urls.length) return null;
    const idPrefix = `lb-${idBase || slugify(title || "galeria")}`;

    return (
        <>
            <style>{`
        .lb-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(140px,1fr));gap:12px}
        .lb-card{border-radius:8px;overflow:hidden;border:1px solid #e5e7eb}
        .lb-thumb{width:100%;height:160px;object-fit:cover;display:block;cursor:zoom-in}
        .lb-overlay{position:fixed;inset:0;background:rgba(0,0,0,.85);display:none;align-items:center;justify-content:center;z-index:9999}
        .lb-overlay:target{display:flex}
        .lb-img{max-width:95vw;max-height:90vh;object-fit:contain;border-radius:8px;box-shadow:0 10px 30px rgba(0,0,0,.4)}
        .lb-close{position:fixed;top:16px;right:16px;color:#fff;border:1px solid rgba(255,255,255,.6);border-radius:6px;padding:6px 10px;text-decoration:none}
        .lb-bg-close{position:fixed;inset:0}
        .lb-img:active{transform:scale(1.5);transition:transform .15s}
      `}</style>

            <div className="mt-3">
                <h6 className="fw-bold mb-2">{title}</h6>

                <div className="lb-grid">
                    {urls.map((rel, i) => {
                        const url = toAbs(rel);
                        const pdf = isPdf(rel);
                        const anchorId = `${idPrefix}-${i}`;

                        return (
                            <div className="lb-card" key={`${rel}-${i}`}>
                                {pdf ? (
                                    <a
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ display: "block", padding: 12, textAlign: "center", textDecoration: "none" }}
                                    >
                                        Abrir PDF
                                    </a>
                                ) : (
                                    <>
                                        <a href={`#${anchorId}`} aria-controls={anchorId} aria-label={`Abrir ${title} ${i + 1}`}>
                                            <img className="lb-thumb" src={url} alt={`${title} ${i + 1}`} loading="lazy" />
                                        </a>

                                        <div id={anchorId} className="lb-overlay" aria-modal="true" role="dialog">
                                            <a href="#_" className="lb-bg-close" aria-label="Fechar"></a>
                                            <img className="lb-img" src={url} alt={`visualização ${i + 1}`} />
                                            <a href="#_" className="lb-close" aria-label="Fechar">
                                                ×
                                            </a>
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </>
    );
}

/** ===== Modal de detalhes (CPF) ===== */
export default function DetailsCpfModal({
    show,
    onHide,
    data,
}: {
    show: boolean;
    onHide: () => void;
    data: IHistory | null;
}) {
    if (!show || !data) return null;

    /** Documento e pessoa */
    const documento =
        (data as any)?.cpf ||
        (data as any)?.document ||
        (data as any)?.documentFormatted ||
        "—";
    const tipoPessoa = "PF";

    /** ConsultationDataPF (armazenado na tabela ConsultationDataPH3A com campos *PF) */
    const cdata = (data as any)?.consultationDataPH3A || {};
    const nome = cdata?.namePF ?? (data as any)?.name ?? "—";
    const mae = cdata?.motherNamePF ?? "—";

    const dr = toObj(cdata?.dataReturn);

    let nascRaw: any =
        (dr?.BirthDate ?? dr?.birthDate) // prioriza o BirthDate do dataReturn
        ?? cdata?.birthDatePF            // fallback antigo
        ?? null;

    // normaliza caso venha no formato /Date(...)/ do .NET
    if (typeof nascRaw === "string" && nascRaw.includes("/Date(")) {
        nascRaw = normalizeDotNetDate(nascRaw);
    }

    // const nasc = nascRaw ? onlyDateBR(nascRaw) : "—";

    const onlyDateBR = (x: unknown) => {
        if (!x) return "—";
        let s = typeof x === "string" ? x : new Date(x as any).toISOString();

        // .NET /Date(1698278400000)/
        const mDotNet = /\/Date\((\d+)\)\//.exec(s);
        if (mDotNet) s = new Date(+mDotNet[1]).toISOString();

        // já no formato BR: "26/10/2002, 00:00:00"
        const mBR = s.match(/(\d{1,2}\/\d{1,2}\/\d{4})/);
        if (mBR) return mBR[1];

        // ISO 8601 → BR
        const mISO = s.match(/(\d{4})-(\d{2})-(\d{2})/);
        if (mISO) return `${mISO[3]}/${mISO[2]}/${mISO[1]}`;

        const d = new Date(s);
        return Number.isNaN(d.getTime())
            ? "—"
            : new Intl.DateTimeFormat("pt-BR").format(d);
    };

    const nasc = onlyDateBR(nascRaw);


    // Score PF
    const scoreAxon = (data as any)?.valueScore4Axon ?? "—";
    const scorePF = typeof cdata?.scorePF === "number" ? cdata.scorePF : undefined;
    const creditScorePF = safeParse(cdata?.creditScorePF);
    const scoreD00 = creditScorePF?.D00 ?? scorePF ?? "—";
    const scoreD90 = creditScorePF?.D90 ?? "—";

    const rendaPresumida = typeof cdata?.presumedIncomePF === "number" ? cdata.presumedIncomePF : undefined;

    // Contatos
    const emailsArr: string[] = (() => {
        const parsed = safeParse(cdata?.emailsPF);
        if (Array.isArray(parsed)) return parsed.map((e: any) => e?.Email ?? e).filter(Boolean);
        return [];
    })();

    const telefonesArr: string[] = (() => {
        const parsed = safeParse(cdata?.phonesPF);
        if (Array.isArray(parsed)) return parsed.map((t: any) => t?.Number ?? t).filter(Boolean);
        return [];
    })();

    const enderecosArr: any[] = (() => {
        const parsed = safeParse(cdata?.addressesPF);
        return Array.isArray(parsed) ? parsed : [];
    })();

    /** TAGS (log de CPF) */
    const tagRecomendacao = (data as any)?.valueTagRecomendacao ?? "—";
    const tagDados =
        (data as any)?.tagDados ??
        (data as any)?.valueTagClusterDadosCPF ??
        "—";
    const tagLiveness =
        (data as any)?.tagLiveness ??
        (data as any)?.valueTagClusterLiveless ??
        (data as any)?.valueTagLiveness ??
        "—";
    const tagBureauFace =
        (data as any)?.tagBureauFace ??
        (data as any)?.valueTagBureauFace ??
        "—";
    const tagDevice =
        (data as any)?.tagDevice ??
        (data as any)?.valueTagDeviceIntelligence ??
        "—";
    const tagDocumento =
        (data as any)?.tagDocumento ??
        (data as any)?.valueTagClusterDocumento ??
        "—";
    const tagFacematch =
        (data as any)?.tagFacematch ??
        (data as any)?.valueTagFacematch ??
        "—";
    const tagAnaliseDocumento =
        (data as any)?.tagAnaliseDocumento ??
        (data as any)?.valueTagDocumentoscopia ??
        "—";

    /** AllowMe + Serasa (mesma estrutura já usada) */
    const livenessRes = (data as any)?.allowMeLivenessResult as
        | { responseJson?: string; createdDate?: string; type?: string; imageUrls?: string[] }
        | undefined;
    const deviceRes = (data as any)?.allowMeDeviceValidationResult as
        | { responseJson?: string; createdDate?: string; type?: string; deviceIp?: string }
        | undefined;
    const documentRes = (data as any)?.allowMeDocumentResult as
        | { responseJson?: string; createdDate?: string; type?: string; imageUrls?: string[] }
        | undefined;

    const liveness = safeParse(livenessRes?.responseJson);
    const device = safeParse(deviceRes?.responseJson);
    const document = safeParse(documentRes?.responseJson);

    const livenessContext = liveness?.contextual;
    const livenessBio = liveness?.biometrics;
    const deviceInfo = device?.DeviceInfo;

    const livenessImages: string[] = Array.isArray(livenessRes?.imageUrls) ? livenessRes!.imageUrls! : [];
    const documentImages: string[] = Array.isArray(documentRes?.imageUrls) ? documentRes!.imageUrls! : [];

    const serasaFaceMatchRes = (data as any)?.allowMeSerasaFaceMatchResult as
        | { responseJson?: string; createdDate?: string; type?: string }
        | undefined;
    const serasaCadastralRes = (data as any)?.allowMeSerasaCadastralResult as
        | { responseJson?: string; createdDate?: string; type?: string }
        | undefined;
    const serasaDocumentoscopiaRes = (data as any)?.allowMeSerasaDocumentoscopiaResult as
        | { responseJson?: string; createdDate?: string; type?: string }
        | undefined;

    const serasaFaceMatch = safeParse(serasaFaceMatchRes?.responseJson);
    const serasaCadastral = safeParse(serasaCadastralRes?.responseJson);
    const serasaDocumentoscopia = safeParse(serasaDocumentoscopiaRes?.responseJson);

    // SERPRO (se estiver presente no seu log para CPF)
    const serpro = (data as any)?.consultationDataSerpro as
        | {
            scoreSerpro?: number | null;
            rejection?: string | null;
            idProduct?: number | null;
            createdDate?: string | null;
            updatedDate?: string | null;
            tagAnaliseBureauFace?: string | null;
        }
        | undefined;

    const bureauIsSerpro =
        !!serpro &&
        (typeof serpro.scoreSerpro === "number" || !!serpro.rejection || (typeof tagBureauFace === "string" && tagBureauFace.trim().length > 0));

    const bureauHeader = bureauIsSerpro ? "Bureau Face (SERPRO · DataValid)" : "Bureau Face (Serasa Cadastral)";

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            size="xl"
            fullscreen="lg-down"
            scrollable
            dialogClassName="details-modal-dialog"
            contentClassName="details-modal-content"
        >
            <Modal.Header closeButton className="dm-header">
                <Modal.Title className="dm-title">Detalhes da Consulta — CPF</Modal.Title>
            </Modal.Header>

            <Modal.Body className="dm-body">
                <Tabs defaultActiveKey="geral" id="details-cpf-tabs" className="dm-tabs mb-3" justify variant="pills">
                    {/* GERAL */}
                    <Tab eventKey="geral" title="GERAL">
                        <Card className="dm-card mb-3">
                            <Card.Body>
                                <Row xs={1} md={2} className="g-3">
                                    <Col>
                                        <Field label="TIPO DE PESSOA" value={tipoPessoa} />
                                    </Col>
                                    <Col>
                                        {/* <Field label="CPF" value={<strong>{documento}</strong>} /> */}
                                        <Field
                                            label="CPF"
                                            value={
                                                <strong>
                                                    {(() => {
                                                        const d = String(documento ?? "").replace(/\D/g, "").slice(0, 11);
                                                        if (d.length !== 11) return "—";
                                                        return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
                                                    })()}
                                                </strong>
                                            }
                                        />
                                    </Col>
                                    <Col>
                                        <Field label="NOME" value={nome} />
                                    </Col>
                                    <Col>
                                        <Field label="DATA DE NASCIMENTO" value={nasc} />
                                    </Col>
                                    <Col>
                                        <Field label="NOME DA MÃE" value={mae} />
                                    </Col>
                                </Row>

                                <hr className="dm-divider" />

                                <div className="d-flex align-items-center gap-2 flex-wrap" style={{ padding: "10px", backgroundColor: "#f8f9fa", borderRadius: 8 }}>
                                    <span className="dm-subtitle" style={{ fontSize: "1.1em", fontWeight: "bold" }}>
                                        RECOMENDAÇÃO 4AXON:
                                    </span>
                                    <Badge className={`dm-badge ${statusToClass(tagRecomendacao)}`} style={{ fontSize: "1.1em", padding: "8px 12px" }}>
                                        {tagRecomendacao}
                                    </Badge>
                                </div>

                                <Row xs={1} md={2} className="g-3 mt-1">
                                    {[
                                        ["DADOS", tagDados],
                                        ["BUREAU FACE", tagBureauFace === "SEM INFORMACAO" ? "Sem informação" : tagBureauFace],
                                        ["LIVENESS", tagLiveness],
                                        ["FACEMATCH", tagFacematch],
                                        ["DEVICE", tagDevice],
                                        ["ANÁLISE DE DOCUMENTO", tagAnaliseDocumento],
                                        ["DOCUMENTO", tagDocumento],
                                    ].map(([label, val]) => (
                                        <Col key={String(label)}>
                                            <Card className="dm-card subtle">
                                                <Card.Body className="d-flex justify-content-between align-items-center">
                                                    <span className="fw-semibold">{label}</span>
                                                    <Badge className={`dm-badge ${statusToClass(val as string)}`}>{val as string}</Badge>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>
                            </Card.Body>
                        </Card>
                    </Tab>

                    {/* DADOS */}
                    <Tab eventKey="dados" title="DADOS">
                        <Row className="g-3">
                            <Col lg={8}>
                                <Card className="dm-card mb-3">
                                    <Card.Body>
                                        <Row xs={1} md={2} className="g-3">
                                            <Col>
                                                <Field label="CPF" value={<strong>{documento}</strong>} />
                                            </Col>
                                            <Col>
                                                <Field label="NOME" value={nome} />
                                            </Col>
                                            <Col>
                                                <Field label="DATA DE NASCIMENTO" value={nasc} />
                                            </Col>
                                            <Col>
                                                <Field label="NOME DA MÃE" value={mae} />
                                            </Col>
                                            <Col>
                                                <Field label="Score D00" value={scoreD00} />
                                            </Col>
                                            <Col>
                                                <Field label="Score D90" value={scoreD90} />
                                            </Col>
                                            <Col>
                                                <Field label="RENDA PRESUMIDA" value={brl(rendaPresumida ?? null)} />
                                            </Col>

                                            {!!emailsArr.length && (
                                                <Col md={12}>
                                                    <Field label="E-MAILS" value={emailsArr.join(", ")} />
                                                </Col>
                                            )}
                                            {!!telefonesArr.length && (
                                                <Col md={12}>
                                                    <Field label="TELEFONES" value={telefonesArr.join(", ")} />
                                                </Col>
                                            )}

                                            {!!enderecosArr.length && (
                                                <Col md={12}>
                                                    <div className="dm-field">
                                                        <small className="dm-label">ENDEREÇOS</small>
                                                        <div className="dm-value">
                                                            <ul className="mb-0 ps-3">
                                                                {enderecosArr.map((a: any, i: number) => {
                                                                    const linha =
                                                                        [
                                                                            a?.Street || a?.Logradouro,
                                                                            a?.Number || a?.Numero,
                                                                            a?.Neighborhood || a?.Bairro,
                                                                            a?.City || a?.Cidade,
                                                                            a?.State || a?.Uf,
                                                                            a?.ZipCode || a?.Cep,
                                                                        ]
                                                                            .filter(Boolean)
                                                                            .join(", ") || "—";
                                                                    return <li key={i}>{linha}</li>;
                                                                })}
                                                            </ul>
                                                        </div>
                                                    </div>
                                                </Col>
                                            )}
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* lateral sticky */}
                            <Col lg={4}>
                                <Card className="dm-card sticky-card">
                                    <Card.Body className="d-flex flex-column justify-content-between" style={{ minHeight: 220 }}>
                                        <div className="text-center my-2">
                                            <div className="text-muted">TAG BUREAU DADOS</div>
                                            <h5 className="mt-2">
                                                <Badge className={`dm-badge ${statusToClass(tagDados)}`}>{tagDados}</Badge>
                                            </h5>
                                        </div>
                                        <div className="dm-right-footer">
                                            <span className="fw-semibold">Score Axon - </span>
                                            <span className="fw-bold">{scoreAxon}</span>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Tab>

                    {/* LIVENESS (AllowMe) */}
                    <Tab eventKey="liveness" title="LIVENESS">
                        <Card className="dm-card">
                            <Card.Header className="dm-card-header d-flex justify-content-between align-items-center">
                                <div className="fw-bold">Liveness</div>
                                <Badge className={`dm-badge ${statusToClass(tagLiveness)}`}>{tagLiveness}</Badge>
                            </Card.Header>
                            <Card.Body>
                                <Row xs={1} md={2} className="g-3">
                                    <Col>
                                        <Field label="Criado em" value={fmtDate(livenessRes?.createdDate)} />
                                    </Col>
                                    <Col>
                                        <div
                                            className={`dm-badge ${allowMeStatusBadgeClass(livenessContext?.status)}`}
                                            style={{ display: "inline-block", padding: "6px 10px", borderRadius: 6 }}
                                        >
                                            Status contextual: {allowMeStatusLabel(livenessContext?.status)}
                                        </div>
                                    </Col>
                                    <Col>
                                        <Field label="Score contextual" value={livenessContext?.score ?? "—"} />
                                    </Col>
                                    <Col>
                                        <Field label="Classificação contextual" value={livenessContext?.score_classification ?? "—"} />
                                    </Col>
                                    <Col>
                                        <Field label="Device ID" value={livenessContext?.device_id ?? "—"} />
                                    </Col>
                                    <Col>
                                        <Field label="IP" value={livenessContext?.ip_address ?? "—"} />
                                    </Col>
                                    <Col>
                                        <Field label="Browser" value={livenessContext?.device_info?.web_browser_name ?? "—"} />
                                    </Col>
                                    <Col>
                                        <Field label="Modelo (web)" value={livenessContext?.device_info?.web_device_model ?? "—"} />
                                    </Col>

                                    <Col>
                                        <div
                                            className={`dm-badge ${allowMeStatusBadgeClass(livenessBio?.liveness?.status)}`}
                                            style={{ display: "inline-block", padding: "6px 10px", borderRadius: 6 }}
                                        >
                                            Liveness.status: {allowMeStatusLabel(livenessBio?.liveness?.status)}
                                        </div>
                                    </Col>
                                    <Col>
                                        <Field label="Liveness.score" value={livenessBio?.liveness?.score ?? "—"} />
                                    </Col>

                                    <Col>
                                        <div
                                            className={`dm-badge ${allowMeStatusBadgeClass(livenessBio?.face_match?.status)}`}
                                            style={{ display: "inline-block", padding: "6px 10px", borderRadius: 6 }}
                                        >
                                            FaceMatch.status: {allowMeStatusLabel(livenessBio?.face_match?.status)}
                                        </div>
                                    </Col>
                                    <Col>
                                        <Field label="FaceMatch.score" value={livenessBio?.face_match?.score ?? "—"} />
                                    </Col>
                                </Row>

                                <Gallery urls={livenessImages} title="Fotos (Liveness)" idBase="liveness" />
                            </Card.Body>
                        </Card>
                    </Tab>

                    {/* DEVICE (AllowMe) */}
                    <Tab eventKey="device" title="DEVICE">
                        <Card className="dm-card">
                            <Card.Header className="dm-card-header">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="fw-bold">Device Validation</div>
                                    <Badge className={`dm-badge ${statusToClass(tagDevice)}`}>{tagDevice}</Badge>
                                </div>
                            </Card.Header>
                            <Card.Body>
                                <Row xs={1} md={2} className="g-3">
                                    <Col>
                                        <Field label="Criado em" value={fmtDate(deviceRes?.createdDate)} />
                                    </Col>
                                    <Col>
                                        <div
                                            className={`dm-badge ${allowMeStatusBadgeClass(device?.Status)}`}
                                            style={{ display: "inline-block", padding: "6px 10px", borderRadius: 6 }}
                                        >
                                            Status: {allowMeStatusLabel(device?.Status)}
                                        </div>
                                    </Col>
                                    <Col>
                                        <Field label="Score" value={device?.Score ?? "—"} />
                                    </Col>
                                    <Col>
                                        <Field label="Classificação" value={device?.ScoreClassification ?? "—"} />
                                    </Col>
                                    <Col>
                                        <Field label="DeviceId" value={device?.DeviceId ?? "—"} />
                                    </Col>
                                    <Col>
                                        <Field label="IP" value={deviceRes?.deviceIp ?? "—"} />
                                    </Col>
                                    <Col>
                                        <Field label="Plataforma" value={deviceInfo?.Platform ?? "—"} />
                                    </Col>
                                    <Col>
                                        <Field label="Browser" value={deviceInfo?.Web_Browser_Name ?? "—"} />
                                    </Col>
                                    <Col>
                                        <Field label="OS Platform" value={deviceInfo?.Web_Os_Platform ?? "—"} />
                                    </Col>
                                    <Col>
                                        <Field label="Versão OS" value={deviceInfo?.Web_Os_Platform_Version ?? "—"} />
                                    </Col>
                                    <Col>
                                        <Field label="Modelo (web)" value={deviceInfo?.Web_Device_Model ?? "—"} />
                                    </Col>
                                    <Col>
                                        <Field
                                            label="Lat/Long"
                                            value={`${deviceInfo?.Device_Location_Latitude ?? 0}, ${deviceInfo?.Device_Location_Longitude ?? 0}`}
                                        />
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Tab>

                    {/* DOCUMENTO (AllowMe) */}
                    <Tab eventKey="documento" title="DOCUMENTO">
                        <Card className="dm-card">
                            <Card.Header className="dm-card-header d-flex justify-content-between align-items-center">
                                <div className="fw-bold">Documento</div>
                                <Badge className={`dm-badge ${statusToClass(tagDocumento)}`}>{tagDocumento}</Badge>
                            </Card.Header>
                            <Card.Body>
                                {(() => {
                                    const documentValidation = safeParse(documentRes?.responseJson)?.document?.validation;
                                    const documentAnalysis: Array<{ type?: string; side?: string }> =
                                        Array.isArray(documentValidation?.analysis) ? documentValidation.analysis : [];

                                    return (
                                        <>
                                            <Row xs={1} md={2} className="g-3">
                                                <Col>
                                                    <Field label="Criado em" value={fmtDate(documentRes?.createdDate)} />
                                                </Col>
                                                <Col>
                                                    <div
                                                        className={`dm-badge ${allowMeStatusBadgeClass(
                                                            safeParse(documentRes?.responseJson)?.document?.status
                                                        )}`}
                                                        style={{ display: "inline-block", padding: "6px 10px", borderRadius: 6 }}
                                                    >
                                                        Status documento:{" "}
                                                        {allowMeStatusLabel(safeParse(documentRes?.responseJson)?.document?.status)}
                                                    </div>
                                                </Col>
                                            </Row>

                                            <Card className="dm-card subtle mt-3">
                                                <Card.Header className="dm-card-header">
                                                    <strong>Análises</strong>
                                                </Card.Header>
                                                <ListGroup variant="flush">
                                                    {documentAnalysis.length ? (
                                                        documentAnalysis.map((a, i) => (
                                                            <ListGroup.Item key={i} className="dm-list-item d-flex justify-content-between">
                                                                <span className="fw-semibold">{a.type ?? "—"}</span>
                                                                <Badge className="dm-badge bg-muted">lado: {a.side ?? "—"}</Badge>
                                                            </ListGroup.Item>
                                                        ))
                                                    ) : (
                                                        <ListGroup.Item className="dm-list-item">
                                                            <span className="text-muted">Nenhuma análise retornada.</span>
                                                        </ListGroup.Item>
                                                    )}
                                                </ListGroup>
                                            </Card>

                                            <Gallery urls={documentImages} title="Arquivos (Documento)" idBase="documento" />
                                        </>
                                    );
                                })()}
                            </Card.Body>
                        </Card>
                    </Tab>

                    {/* BUREAU FACE */}
                    <Tab eventKey="bureauface" title="BUREAU FACE">
                        <Card className="dm-card">
                            <Card.Header className="dm-card-header d-flex justify-content-between align-items-center">
                                <div className="fw-bold">{bureauHeader}</div>
                                <Badge className={`dm-badge ${statusToClass(tagBureauFace)}`}>
                                    {tagBureauFace === "SEM INFORMACAO" ? "Sem informação" : tagBureauFace}
                                </Badge>
                            </Card.Header>
                            <Card.Body>
                                {bureauIsSerpro ? (
                                    <Row xs={1} md={2} className="g-3">
                                        <Col>
                                            <Field label="Criado em" value={fmtDate(serpro?.createdDate ?? serpro?.updatedDate ?? undefined)} />
                                        </Col>
                                        <Col>
                                            <Field label="CPF" value={documento} />
                                        </Col>
                                        <Col>
                                            <Field
                                                label="Score (SERPRO)"
                                                value={typeof serpro?.scoreSerpro === "number" ? `${serpro!.scoreSerpro}%` : "—"}
                                            />
                                        </Col>
                                        <Col>
                                            <Field
                                                label="Rejeição"
                                                value={
                                                    serpro?.rejection ? (
                                                        <a
                                                            href="https://apicenter.estaleiro.serpro.gov.br/documentacao/datavalid/codigos_retorno/#422-requisicao-nao-processada"
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            {serpro.rejection}
                                                        </a>
                                                    ) : (
                                                        "—"
                                                    )
                                                }
                                            />
                                        </Col>
                                        <Col>
                                            <Field label="Tag (calculada)" value={tagBureauFace} />
                                        </Col>
                                    </Row>
                                ) : (
                                    <Row xs={1} md={2} className="g-3">
                                        <Col>
                                            <Field label="Criado em" value={fmtDate(serasaCadastralRes?.createdDate)} />
                                        </Col>
                                        <Col>
                                            <Field label="ID" value={serasaCadastral?.id ?? "—"} />
                                        </Col>
                                        <Col>
                                            <Field label="Rank" value={serasaCadastral?.rank ?? "—"} />
                                        </Col>
                                        <Col>
                                            <Field label="CPF" value={serasaCadastral?.cpf ?? "—"} />
                                        </Col>
                                    </Row>
                                )}
                            </Card.Body>
                        </Card>
                    </Tab>

                    {/* FACEMATCH */}
                    <Tab eventKey="facematch" title="FACEMATCH">
                        <Card className="dm-card">
                            <Card.Header className="dm-card-header">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="fw-bold">Facematch (Serasa)</div>
                                    <Badge className={`dm-badge ${statusToClass(tagFacematch)}`}>{tagFacematch}</Badge>
                                </div>
                            </Card.Header>
                            <Card.Body>
                                <Row xs={1} md={2} className="g-3">
                                    <Col>
                                        <Field label="Criado em" value={fmtDate(serasaFaceMatchRes?.createdDate)} />
                                    </Col>
                                    <Col>
                                        <Field label="CPF" value={serasaFaceMatch?.cpf ?? "—"} />
                                    </Col>
                                    <Col>
                                        <Field label="Rank" value={serasaFaceMatch?.rank ?? "—"} />
                                    </Col>
                                    <Col>
                                        <Field label="Resultado" value={serasaFaceMatch?.result ?? "—"} />
                                    </Col>
                                    <Col>
                                        <Field
                                            label="Informações adicionais"
                                            value={Array.isArray(serasaFaceMatch?.additionalInformation) ? serasaFaceMatch.additionalInformation.join(", ") : "—"}
                                        />
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Tab>

                    {/* ANÁLISE DE DOCUMENTO (Serasa) */}
                    <Tab eventKey="analisedocumento" title="ANÁLISE DE DOCUMENTO">
                        <Card className="dm-card">
                            <Card.Header className="dm-card-header d-flex justify-content-between align-items-center">
                                <div className="fw-bold">Análise de documento</div>
                                <Badge className={`dm-badge ${statusToClass(tagAnaliseDocumento)}`}>{tagAnaliseDocumento}</Badge>
                            </Card.Header>
                            <Card.Body>
                                <Row xs={1} md={2} className="g-3">
                                    <Col>
                                        <Field label="Criado em" value={fmtDate(serasaDocumentoscopiaRes?.createdDate)} />
                                    </Col>
                                    <Col>
                                        <Field label="ID" value={serasaDocumentoscopia?.id ?? "—"} />
                                    </Col>
                                    <Col>
                                        <Field label="CPF" value={serasaDocumentoscopia?.cpf ?? "—"} />
                                    </Col>
                                    <Col>
                                        <Field label="Rank" value={serasaDocumentoscopia?.rank ?? "—"} />
                                    </Col>
                                    <Col>
                                        <Field label="Resultado" value={serasaDocumentoscopia?.result ?? "—"} />
                                    </Col>
                                    <Col>
                                        <Field
                                            label="Informações adicionais"
                                            value={
                                                Array.isArray(serasaDocumentoscopia?.additionalInformation)
                                                    ? serasaDocumentoscopia.additionalInformation.join(", ")
                                                    : "—"
                                            }
                                        />
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Tab>
                </Tabs>
            </Modal.Body>
        </Modal>
    );
}